import { getENV } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import { type FormFields, usePurchaseFormValue } from '@/entities/purchase-form';
import { dataLayerPush } from '@/shared/analytics';

export const useEEaddToCartAnalytics = () => {
    const { product, base, tailored } = useProduct();

    const getValues = usePurchaseFormValue();

    function sendAnalytics() {
        if (!product || !base || !tailored) return;

        const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';

        const { categories, merchandisingHierarchy } = base;
        const { sku, name, brand, variantValues } = product;

        const quantity = getValues('quantity');
        const price = product.price.sale ?? product.price.original;

        const tailoredBopisStoreId = tailored.variants?.[sku].BOPIS?.storeId;

        const selectedPurchaseOption = getValues('purchaseOption');
        const selectedDeliveryMethod = getValues('fulfilmentMethod');
        const selectedAutoshipFrequency = getValues('autoshipFrequency');

        const isAutoship = selectedPurchaseOption === 'autoship';
        const deliveryMethodKey = selectedDeliveryMethod.toUpperCase() as Uppercase<FormFields['fulfilmentMethod']>;
        const storeId = deliveryMethodKey === 'BOPIS' ? tailoredBopisStoreId?.padStart(4, '0') : null;

        const customCaseQuantityPatch = product?.deliveryMethods[deliveryMethodKey].purchaseQuantities?.min;

        // Clear ecommerce object to prevent GTM from sending stale values with new events
        dataLayerPush({
            ecommerce: null,
        });

        dataLayerPush({
            event: 'EEaddToCart',
            ecommerce: {
                currencyCode: isCA ? 'CAD' : 'USD',
                add: {
                    actionField: {
                        list: 'PFM - Standard Add to Cart',
                    },
                    products: [
                        {
                            name,
                            id: product.sku,
                            brand: brand?.name ?? '',
                            dimension5: product.pharmacy?.type ?? 'Other',
                            dimension6: 'Standard PDP',
                            dimension9: categories[0] ?? '',
                            dimension10: categories[1] ?? '',
                            dimension11: categories[2] ?? '',
                            dimension12: isAutoship ? 'autoship' : selectedDeliveryMethod,
                            dimension13: isAutoship ? Number(selectedAutoshipFrequency) : null,
                            dimension14: storeId ?? null,
                            dimension18: variantValues?.color ?? null,
                            dimension19: variantValues?.size ?? null,
                            dimension20: variantValues?.flavor ?? null,
                            dimension21:
                                product.pharmacy?.type === 'rx_med' && customCaseQuantityPatch
                                    ? customCaseQuantityPatch.toString()
                                    : null,
                            dimension41: merchandisingHierarchy?.vicePresidentName ?? '',
                            dimension42: merchandisingHierarchy?.division ?? '',
                            dimension43: merchandisingHierarchy?.department ?? '',
                            dimension44: merchandisingHierarchy?.class ?? '',
                            dimension45: merchandisingHierarchy?.category ?? '',
                            dimension133: 'PFM - Standard Add to Cart',
                            price,
                            quantity,
                        },
                    ],
                },
            },
        });
    }

    return { sendAnalytics };
};
